import React, { useMemo, useState } from "react";
import { useData } from "../../hooks/useData";
import { useAuth } from "../../context/AuthContext";
import { useOrgSetterIds } from "../../hooks/useOrgSetterIds";
import { useProfiles } from "../../hooks/useProfiles";
import { useMarkedLeads, MARKED_ALL, type MarkedLead } from "../../hooks/useMarkedLeads";
import { MarkedLeadsSplitView, HAS_MONEY } from "./MarkedLeadsSplitView";

type Verdict = MarkedLead["dmread_verdict"];
const VERDICTS: { key: Verdict; label: string; on: string }[] = [
  {
    key: "QUALIFIABLE",
    label: "Marked good",
    on: "bg-ds-green/10 text-ds-green border-ds-green/30",
  },
  { key: "BORDERLINE", label: "Borderline", on: "bg-ds-amber/10 text-ds-amber border-ds-amber/30" },
  {
    key: "REAL_DQ",
    label: "Not a fit",
    on: "bg-black/[0.06] text-glass-secondary border-black/[0.12]",
  },
];

const chipBase =
  "anim-tap token-13 font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap";
const chipOff = "bg-black/[0.03] text-glass-muted border-black/[0.08]";

const MarkedLeadsTab: React.FC = () => {
  const { currentOrganization } = useData();
  const { user } = useAuth();
  const orgId = currentOrganization?.id ?? null;
  const callerGhl = user?.ghlUserId ?? null;

  const { setterIds } = useOrgSetterIds(orgId);
  const { getProfileByGhlId } = useProfiles(orgId);

  // The viewing bar lists DM SETTERS only (role=setter). A caller who is not a DM
  // setter (exec / super-admin like Mako or Nathaniel) has no own queue — hide the
  // "you" chip for them and land them on Everyone. A real DM setter still opens on
  // their own queue (D5 default).
  const callerIsSetter = !!callerGhl && setterIds.includes(callerGhl);

  // Viewing target. `picked` = the user's explicit choice, undefined until they click.
  // `target` derives the default from role (setter -> own queue, non-setter -> Everyone),
  // so there is no setState-in-effect race. null = own-queue, MARKED_ALL = Everyone,
  // else a peer ghl_user_id.
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const target = picked === undefined ? (callerIsSetter ? null : MARKED_ALL) : picked;
  const { rows, loading, error, refresh } = useMarkedLeads(orgId, target);

  // filters (Mako's 2 named axes are interactive; geo/life are inferred selects)
  const [activeVerdicts, setActiveVerdicts] = useState<Set<Verdict>>(
    () => new Set<Verdict>(["QUALIFIABLE", "BORDERLINE", "REAL_DQ"])
  );
  const [moneyOnly, setMoneyOnly] = useState(false);
  const [geo, setGeo] = useState("");
  const [life, setLife] = useState("");

  const geos = useMemo(
    () => Array.from(new Set(rows.map((r) => r.dmread_geo).filter(Boolean))).sort() as string[],
    [rows]
  );
  const lifes = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.dmread_life_stage).filter(Boolean))).sort() as string[],
    [rows]
  );

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          activeVerdicts.has(r.dmread_verdict) &&
          (!moneyOnly ||
            (r.dmread_money_signal !== null && HAS_MONEY.has(r.dmread_money_signal))) &&
          (!geo || r.dmread_geo === geo) &&
          (!life || r.dmread_life_stage === life)
      ),
    [rows, activeVerdicts, moneyOnly, geo, life]
  );

  const toggleVerdict = (v: Verdict) =>
    setActiveVerdicts((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  const peers = setterIds.filter((id) => id !== callerGhl);
  // RPC caps Everyone at 200, own/peer at 500 — flag when the result is truncated.
  const atCap = rows.length >= (target === MARKED_ALL ? 200 : 500);

  if (!currentOrganization) {
    return <div className="p-8 text-center text-glass-muted">Please select an organization.</div>;
  }

  return (
    <div className="space-y-4">
      {/* VIEWING BAR (D5) — own-queue default; view any peer or Everyone, act own-only */}
      <div className="flex items-center gap-2 flex-wrap rounded-xl liquid-glass-light px-3 py-2">
        <span className="token-11 font-bold uppercase tracking-wide text-glass-muted mr-1">
          Viewing
        </span>
        {callerIsSetter && (
          <button
            type="button"
            data-testid="viewing-you"
            onClick={() => setPicked(null)}
            aria-pressed={target === null}
            className={`anim-tap token-13 font-semibold px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${
              target === null
                ? "bg-ds-accent/10 text-ds-accent border-ds-accent/30"
                : "bg-black/[0.03] text-glass-muted border-black/[0.08]"
            }`}
          >
            {getProfileByGhlId(callerGhl ?? "")?.full_name || "Me"}
            <span className="token-11 font-bold uppercase bg-ds-accent text-white px-1.5 rounded">
              you
            </span>
          </button>
        )}
        {peers.map((id) => (
          <button
            key={id}
            type="button"
            data-testid="viewing-peer"
            onClick={() => setPicked(id)}
            aria-pressed={target === id}
            className={`anim-tap token-13 font-semibold px-3 py-1.5 rounded-lg border ${
              target === id
                ? "bg-ds-accent/10 text-ds-accent border-ds-accent/30"
                : "bg-black/[0.03] text-glass-muted border-black/[0.08]"
            }`}
          >
            {getProfileByGhlId(id)?.full_name || "Setter"}
          </button>
        ))}
        <button
          type="button"
          data-testid="viewing-everyone"
          onClick={() => setPicked(MARKED_ALL)}
          aria-pressed={target === MARKED_ALL}
          className={`anim-tap token-13 font-semibold px-3 py-1.5 rounded-lg border ${
            target === MARKED_ALL
              ? "bg-ds-green/10 text-ds-green border-ds-green/30"
              : "bg-black/[0.03] text-glass-muted border-black/[0.08]"
          }`}
        >
          Everyone
        </button>
        {target !== null && (
          <span className="token-11 text-glass-muted ml-1">
            {target === MARKED_ALL
              ? "Read-only — reply only on your own leads."
              : "Peer queue — read-only."}
          </span>
        )}
      </div>

      {/* FILTERS — verdict + money are Mako's named axes; geo/life are inferred */}
      <div className="flex items-center gap-2 flex-wrap rounded-xl liquid-glass-light px-3 py-2">
        {VERDICTS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => toggleVerdict(v.key)}
            aria-pressed={activeVerdicts.has(v.key)}
            className={`${chipBase} ${activeVerdicts.has(v.key) ? v.on : chipOff}`}
          >
            {v.label}
          </button>
        ))}
        <span className="w-px h-5 bg-black/[0.1] mx-1" />
        <button
          type="button"
          data-testid="filter-money"
          onClick={() => setMoneyOnly((m) => !m)}
          aria-pressed={moneyOnly}
          className={`${chipBase} ${moneyOnly ? "bg-ds-amber/10 text-ds-amber border-ds-amber/30" : chipOff}`}
        >
          Has money
        </button>
        <span className="w-px h-5 bg-black/[0.1] mx-1" />
        <span className="token-11 text-ds-accent">~ inferred by Claude:</span>
        <select
          value={geo}
          onChange={(e) => setGeo(e.target.value)}
          className="token-13 rounded-lg border border-black/[0.1] bg-black/[0.02] px-2 py-1 text-glass-secondary"
        >
          <option value="">All geo</option>
          {geos.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <select
          value={life}
          onChange={(e) => setLife(e.target.value)}
          className="token-13 rounded-lg border border-black/[0.1] bg-black/[0.02] px-2 py-1 text-glass-secondary"
        >
          <option value="">All life-stage</option>
          {lifes.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <span className="ml-auto token-13 text-glass-muted">
          <b className="text-glass font-bold">{filtered.length}</b> of {rows.length}
          {atCap && <span className="text-ds-amber"> · showing top {rows.length}</span>}
        </span>
      </div>

      {/* BODY */}
      {error && (
        <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-4 text-ds-red flex items-center justify-between">
          <span>Error: {error}</span>
          <button onClick={refresh} className="anim-tap token-14 underline">
            Retry
          </button>
        </div>
      )}
      {loading && rows.length === 0 ? (
        <div className="p-8 text-center text-glass-muted token-14">Loading marked leads…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-glass-muted token-14">
          {target === null
            ? "No leads assigned to you yet."
            : target === MARKED_ALL
              ? "No marked leads in the org right now."
              : "No marked leads for this setter."}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-glass-muted token-14">Inbox zero — nice.</div>
      ) : (
        <MarkedLeadsSplitView
          rows={filtered}
          timezone={currentOrganization.timezone || "UTC"}
          organizationId={currentOrganization.id}
        />
      )}
    </div>
  );
};

export default MarkedLeadsTab;
